import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Users,
  Activity,
  Eye,
  ShoppingCart,
  DollarSign,
  RefreshCw,
  TrendingUp,
  Globe,
  Map,
  BarChart2,
  ChevronDown,
  AlertCircle,
  Loader2,
  MousePointerClick,
  CreditCard,
  Package,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com/api/pixel/analytics';

// Helper: format large numbers
function fmtNum(n) {
  if (n === undefined || n === null) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(Number(n).toLocaleString('en-US'));
}

// Helper: format currency
function fmtCurrency(n) {
  if (n === undefined || n === null) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Helper: format date to YYYY-MM-DD
function toDateStr(date) {
  return date.toISOString().slice(0, 10);
}

// Helper: default last-30-days range
function defaultDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { startDate: toDateStr(start), endDate: toDateStr(end) };
}

// Generic fetcher with JWT cookie auth
async function apiFetch(url) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json();
}

// Build query string helper
function buildQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, v);
  });
  return q.toString() ? '?' + q.toString() : '';
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color = 'blue', subValue }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
    teal: 'bg-teal-50 text-teal-600',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-start gap-4">
      <div className={`p-3 rounded-lg ${colorMap[color] || colorMap.blue}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide truncate">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5 leading-tight">{value}</p>
        {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
      {children}
    </h2>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// ─── Funnel step widths ───────────────────────────────────────────────────────
const FUNNEL_STEPS = [
  { key: 'page_view', label: 'Page View', icon: Eye, color: 'bg-blue-500' },
  { key: 'product_viewed', label: 'Product Viewed', icon: MousePointerClick, color: 'bg-indigo-500' },
  { key: 'add_to_cart', label: 'Add to Cart', icon: ShoppingCart, color: 'bg-violet-500' },
  { key: 'checkout', label: 'Checkout', icon: CreditCard, color: 'bg-purple-500' },
  { key: 'purchase', label: 'Purchase', icon: Package, color: 'bg-fuchsia-500' },
];

function FunnelSection({ data, loading, error }) {
  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBox message={error} />;
  if (!data) return null;

  // Backend returns { funnel: [{ stage, uniqueVisitors, ... }] }
  const funnelArr = data?.funnel || [];
  const funnelMap = {};
  funnelArr.forEach(item => { funnelMap[item.stage] = item.uniqueVisitors ?? 0; });

  const steps = FUNNEL_STEPS.map((s) => ({
    ...s,
    count: funnelMap[s.key] ?? 0,
  }));

  const maxVal = steps[0]?.count || 1;

  return (
    <div className="space-y-3">
      {steps.map((step, idx) => {
        const pct = maxVal > 0 ? Math.round((step.count / maxVal) * 100) : 0;
        const dropoff =
          idx > 0 && steps[idx - 1].count > 0
            ? Math.round((1 - step.count / steps[idx - 1].count) * 100)
            : null;

        return (
          <div key={step.key}>
            {dropoff !== null && (
              <div className="text-xs text-red-500 ml-1 mb-1">
                -{dropoff}% drop-off from previous step
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className={`p-1.5 rounded ${step.color} text-white`}>
                <step.icon className="w-3.5 h-3.5" />
              </div>
              <span className="text-sm text-gray-700 w-36 shrink-0">{step.label}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-5 relative">
                <div
                  className={`${step.color} h-5 rounded-full transition-all duration-500`}
                  style={{ width: `${pct}%`, minWidth: step.count > 0 ? '2%' : '0' }}
                />
              </div>
              <span className="text-sm font-semibold text-gray-800 w-20 text-right shrink-0">
                {fmtNum(step.count)}
              </span>
              <span className="text-xs text-gray-400 w-10 text-right shrink-0">{pct}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PixelAnalytics() {
  // Date range state
  const [startDate, setStartDate] = useState(defaultDates().startDate);
  const [endDate, setEndDate] = useState(defaultDates().endDate);

  // Brand state — automatically use currently selected brand
  const [brandId, setBrandId] = useState(
    () => localStorage.getItem('currentBrandId') || ''
  );

  // Data states
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);

  const [eventsBreakdown, setEventsBreakdown] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);

  const [sources, setSources] = useState(null);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState(null);

  const [dailyTrend, setDailyTrend] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState(null);

  const [topPages, setTopPages] = useState(null);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState(null);

  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelError, setFunnelError] = useState(null);

  const [geo, setGeo] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState(null);

  // Fetch all analytics data
  const fetchAll = useCallback(async () => {
    if (!brandId) return;
    const q = buildQuery({ brandId, startDate, endDate });

    setOverviewLoading(true);
    setOverviewError(null);
    apiFetch(`${API_BASE}/overview${q}`)
      .then(setOverview)
      .catch((e) => setOverviewError(e.message))
      .finally(() => setOverviewLoading(false));

    setEventsLoading(true);
    setEventsError(null);
    apiFetch(`${API_BASE}/events-breakdown${q}`)
      .then(setEventsBreakdown)
      .catch((e) => setEventsError(e.message))
      .finally(() => setEventsLoading(false));

    setSourcesLoading(true);
    setSourcesError(null);
    apiFetch(`${API_BASE}/sources${q}`)
      .then(setSources)
      .catch((e) => setSourcesError(e.message))
      .finally(() => setSourcesLoading(false));

    setDailyLoading(true);
    setDailyError(null);
    apiFetch(`${API_BASE}/daily-trend${q}`)
      .then(setDailyTrend)
      .catch((e) => setDailyError(e.message))
      .finally(() => setDailyLoading(false));

    setPagesLoading(true);
    setPagesError(null);
    apiFetch(`${API_BASE}/top-pages${q}`)
      .then(setTopPages)
      .catch((e) => setPagesError(e.message))
      .finally(() => setPagesLoading(false));

    setFunnelLoading(true);
    setFunnelError(null);
    apiFetch(`${API_BASE}/funnel${q}`)
      .then(setFunnel)
      .catch((e) => setFunnelError(e.message))
      .finally(() => setFunnelLoading(false));

    setGeoLoading(true);
    setGeoError(null);
    apiFetch(`${API_BASE}/geo${q}`)
      .then(setGeo)
      .catch((e) => setGeoError(e.message))
      .finally(() => setGeoLoading(false));
  }, [brandId, startDate, endDate]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Listen for brand changes from other components (e.g. sidebar brand selector)
  useEffect(() => {
    const interval = setInterval(() => {
      const current = localStorage.getItem('currentBrandId') || '';
      if (current !== brandId) setBrandId(current);
    }, 1000);
    return () => clearInterval(interval);
  }, [brandId]);

  // Extract arrays from backend responses (each endpoint wraps data in a named key)
  const eventsArr = (eventsBreakdown?.breakdown || []).sort((a, b) => (b.eventCount ?? 0) - (a.eventCount ?? 0));
  const sourcesArr = (sources?.sources || []).sort((a, b) => (b.sessions ?? 0) - (a.sessions ?? 0));
  const pagesArr = (topPages?.pages || []).slice(0, 20);
  const geoCities = (geo?.cities || []).slice(0, 20);
  const trendArr = (dailyTrend?.trend || []).map((row) => ({
    ...row,
    date: (row.date || '').toString().slice(0, 10),
  }));

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Activity className="w-6 h-6 text-blue-600" />
          Pixel Analytics
        </h1>
        <p className="text-sm text-gray-500 mt-1">Real-time visitor and conversion tracking powered by Glowytics Pixel.</p>
      </div>

      <div className="space-y-6">
        {/* Filter bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500 font-medium">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={fetchAll}
              disabled={!brandId}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Overview cards */}
        <section>
          <SectionTitle>
            <TrendingUp className="w-4 h-4 text-gray-500" />
            Overview
          </SectionTitle>
          {overviewLoading && <LoadingSpinner />}
          {overviewError && <ErrorBox message={overviewError} />}
          {!overviewLoading && overview && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Visitors" value={fmtNum(overview.uniqueVisitors)} color="blue" />
              <StatCard icon={Activity} label="Total Sessions" value={fmtNum(overview.totalSessions)} color="green" />
              <StatCard icon={Eye} label="Page Views" value={fmtNum(overview.pageViews)} color="purple" />
              <StatCard
                icon={DollarSign}
                label="Revenue / Purchases"
                value={fmtCurrency(overview.revenue)}
                subValue={`${fmtNum(overview.purchases)} purchases`}
                color="orange"
              />
            </div>
          )}
        </section>

        {/* Daily Trend Chart */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <SectionTitle>
            <TrendingUp className="w-4 h-4 text-gray-500" />
            Daily Trend
          </SectionTitle>
          {dailyLoading && <LoadingSpinner />}
          {dailyError && <ErrorBox message={dailyError} />}
          {!dailyLoading && !dailyError && trendArr.length > 0 && (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendArr} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} width={45} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} labelStyle={{ fontWeight: 600, color: '#111827' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="visitors" name="Visitors" stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="sessions" name="Sessions" stroke="#10b981" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="purchases" name="Purchases" stroke="#f97316" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
          {!dailyLoading && !dailyError && trendArr.length === 0 && brandId && (
            <p className="text-sm text-gray-400 text-center py-8">No trend data for this period.</p>
          )}
        </section>

        {/* Event Breakdown */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <SectionTitle>
            <BarChart2 className="w-4 h-4 text-gray-500" />
            Event Breakdown
          </SectionTitle>
          {eventsLoading && <LoadingSpinner />}
          {eventsError && <ErrorBox message={eventsError} />}
          {!eventsLoading && !eventsError && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left">
                    {['Event Type', 'Total', 'Unique Visitors', 'Unique Sessions'].map((h) => (
                      <th key={h} className="pb-2 pr-6 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {eventsArr.length === 0 && (
                    <tr><td colSpan={4} className="py-6 text-center text-gray-400">No data.</td></tr>
                  )}
                  {eventsArr.map((row, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-2.5 pr-6">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{row.event || '—'}</span>
                      </td>
                      <td className="py-2.5 pr-6 font-semibold text-gray-800">{fmtNum(row.eventCount)}</td>
                      <td className="py-2.5 pr-6 text-gray-600">{fmtNum(row.uniqueVisitors)}</td>
                      <td className="py-2.5 pr-6 text-gray-600">{fmtNum(row.uniqueSessions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Traffic Sources */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <SectionTitle>
            <Globe className="w-4 h-4 text-gray-500" />
            Traffic Sources
          </SectionTitle>
          {sourcesLoading && <LoadingSpinner />}
          {sourcesError && <ErrorBox message={sourcesError} />}
          {!sourcesLoading && !sourcesError && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left">
                    {['Source', 'Medium', 'Campaign', 'Sessions', 'Visitors', 'Page Views', 'Add to Cart', 'Purchases', 'Revenue', 'gclid', 'fbclid'].map((h) => (
                      <th key={h} className="pb-2 pr-4 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sourcesArr.length === 0 && (
                    <tr><td colSpan={11} className="py-6 text-center text-gray-400">No data.</td></tr>
                  )}
                  {sourcesArr.map((row, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-2.5 pr-4 font-medium text-gray-800 whitespace-nowrap">{row.source || '(direct)'}</td>
                      <td className="py-2.5 pr-4 text-gray-600 whitespace-nowrap">{row.medium || '—'}</td>
                      <td className="py-2.5 pr-4 text-gray-500 whitespace-nowrap max-w-[150px] truncate">{row.campaign || '—'}</td>
                      <td className="py-2.5 pr-4 text-gray-700">{fmtNum(row.sessions)}</td>
                      <td className="py-2.5 pr-4 text-gray-700">{fmtNum(row.uniqueVisitors)}</td>
                      <td className="py-2.5 pr-4 text-gray-700">{fmtNum(row.pageViews)}</td>
                      <td className="py-2.5 pr-4 text-gray-700">{fmtNum(row.addToCarts)}</td>
                      <td className="py-2.5 pr-4 text-gray-700">{fmtNum(row.purchases)}</td>
                      <td className="py-2.5 pr-4 text-gray-700">{fmtCurrency(row.revenue)}</td>
                      <td className="py-2.5 pr-4 text-gray-500">{fmtNum(row.gclidCount)}</td>
                      <td className="py-2.5 pr-4 text-gray-500">{fmtNum(row.fbclidCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Conversion Funnel */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <SectionTitle>
            <ChevronDown className="w-4 h-4 text-gray-500" />
            Conversion Funnel
          </SectionTitle>
          <FunnelSection data={funnel} loading={funnelLoading} error={funnelError} />
          {!funnelLoading && !funnelError && !funnel && brandId && (
            <p className="text-sm text-gray-400 text-center py-8">No funnel data available.</p>
          )}
        </section>

        {/* Bottom row: Top Pages + Geo */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Pages */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <SectionTitle>
              <Eye className="w-4 h-4 text-gray-500" />
              Top Pages
            </SectionTitle>
            {pagesLoading && <LoadingSpinner />}
            {pagesError && <ErrorBox message={pagesError} />}
            {!pagesLoading && !pagesError && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left">
                      {['Page Path', 'Views', 'Unique Visitors'].map((h) => (
                        <th key={h} className="pb-2 pr-4 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagesArr.length === 0 && (
                      <tr><td colSpan={3} className="py-6 text-center text-gray-400">No data.</td></tr>
                    )}
                    {pagesArr.map((row, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-2 pr-4 text-gray-700 font-mono text-xs max-w-[200px] truncate" title={row.pagePath}>{row.pagePath || '—'}</td>
                        <td className="py-2 pr-4 text-gray-800 font-semibold">{fmtNum(row.views)}</td>
                        <td className="py-2 pr-4 text-gray-600">{fmtNum(row.uniqueVisitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Geo */}
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <SectionTitle>
              <Map className="w-4 h-4 text-gray-500" />
              Geographic Distribution
            </SectionTitle>
            {geoLoading && <LoadingSpinner />}
            {geoError && <ErrorBox message={geoError} />}
            {!geoLoading && !geoError && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left">
                      {['Country', 'City', 'Visitors', 'Events'].map((h) => (
                        <th key={h} className="pb-2 pr-4 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {geoCities.length === 0 && (
                      <tr><td colSpan={4} className="py-6 text-center text-gray-400">No data.</td></tr>
                    )}
                    {geoCities.map((row, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-2 pr-4 text-gray-800 font-medium">{row.country || '—'}</td>
                        <td className="py-2 pr-4 text-gray-600">{row.city || '—'}</td>
                        <td className="py-2 pr-4 text-gray-700 font-semibold">{fmtNum(row.visitors)}</td>
                        <td className="py-2 pr-4 text-gray-600">{fmtNum(row.events)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
